// PushNotificationHandler.jsx
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import VisibilityManager from '../resource/VisibilityManager.js';
import { getIsSafari } from '../resource/getUserAgent.js';
import { pushNotification as pushNotificationLog } from '../resource/debug.js';

const log = pushNotificationLog.extend('log');

const isSafari = getIsSafari();

export const PushNotificationHandler = ({
  isAuthed = false,
  fcmToken = '',
  pushyToken = '',
  permission = '',
  pushyAppId = '',
  submitPushNotificationToken = () => null,
  setFcmToken = () => null,
  setPushyToken = () => null,
}) => {
  const prevPermission = useRef(permission);
  const prevPushyAddId = useRef(pushyAppId);
  const visibilityManager = useRef(null);

  const shouldSetFcmToken = () =>
    window.PushManager && permission === 'granted';

  const shouldSetPushyToken = () => permission !== 'denied';

  const handlePermission = () => {
    log('permission changed', {
      prevPermission: prevPermission.current,
      permission,
    });
    if (prevPermission.current !== permission && permission === 'granted') {
      if (shouldSetFcmToken()) {
        log('get the new fcm token');
        setFcmToken();
      }
      if (shouldSetPushyToken()) {
        log('get the new pushy token');
        setPushyToken();
      }
    }
    prevPermission.current = permission;
  };

  // after login
  useEffect(() => {
    if (isAuthed) {
      if (shouldSetFcmToken()) {
        log('get new fcm token after login');
        setFcmToken();
      }
      if (shouldSetPushyToken()) {
        log('get new pushy token after login');
        setPushyToken();
      }

      if (isSafari)
        visibilityManager.current = new VisibilityManager({
          visibleListener: handlePermission,
        });
    }
    return () => {
      visibilityManager.current?.unbindListeners();
    };
  }, [isAuthed]);

  // after login, and get new fcmToken or new pushyToken
  useEffect(() => {
    if (isAuthed && (fcmToken || pushyToken)) {
      log('submit push notification token', { fcmToken, pushyToken });
      submitPushNotificationToken({ fcmToken, pushyToken });
    }
  }, [isAuthed, fcmToken, pushyToken]);

  useEffect(() => {
    handlePermission();
  }, [permission]);

  useEffect(() => {
    if (prevPushyAddId.current !== pushyAppId) {
      if (shouldSetPushyToken()) setPushyToken();
      prevPushyAddId.current = pushyAppId;
    }
  }, [pushyAppId]);

  return null;
};

PushNotificationHandler.propTypes = {
  isAuthed: PropTypes.bool,
  fcmToken: PropTypes.string,
  pushyToken: PropTypes.string,
  permission: PropTypes.string,
  pushyAppId: PropTypes.string,
  submitPushNotificationToken: PropTypes.func,
  setFcmToken: PropTypes.func,
  setPushyToken: PropTypes.func,
};

export default PushNotificationHandler;
